import { Injectable, inject} from '@angular/core';
import { Product, ProductWinner, UnpaidHandlingOption } from 'src/app/core/models/Product';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { Observable, map, BehaviorSubject } from 'rxjs';
import { ApiUrlService } from '../api/api-url.service';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private readonly apiUrlService = inject(ApiUrlService);
  private get productsApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/products`); }
  private get productWinnersApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/product-winners`); }
  private charityProductsSubject = new BehaviorSubject<Product[]>([]);
  charityProducts$ = this.charityProductsSubject.asObservable();
  private auctionProductsSubject = new BehaviorSubject<Product[]>([]);
  auctionProducts$ = this.auctionProductsSubject.asObservable();
  private bidProductsSubject = new BehaviorSubject<Product[]>([]);
  bidProducts$ = this.bidProductsSubject.asObservable();
  private winningProductsRefreshSubject = new BehaviorSubject<void>(undefined);
  winningProductsRefresh$ = this.winningProductsRefreshSubject.asObservable();

  constructor(private httpClient: HttpClientWrapperService) {}

  getAllProducts(): Observable<Product[]> {
    return this.httpClient.get<Product[]>(this.productsApiUrl);
  }
  
  getAllMarketplaceProducts(): Observable<Product[]> {
    return this.httpClient.get<Product[]>(this.productsApiUrl).pipe(
      map(products => products.filter(product => product.acceptsBids === false))
    );
  }

  getProducts(acceptsBids?: boolean): Observable<Product[]> {
    return this.httpClient.get<Product[]>(`${this.productsApiUrl}?acceptsBids=${acceptsBids}`);
  }

  // Will bring back with limits once get products returns the newest items first
  getLimitedProducts(
    offset: number = 0,
    limit?: number,
    acceptsBids?: boolean
  ): Observable<Product[]> {
    return this.httpClient.get<Product[]>(`${this.productsApiUrl}?acceptsBids=${acceptsBids}&limit=${limit}&offset=${offset}`);
  }

  loadMarketplaceProductsByCharityId(charityId: number): void {
    this.getMarketplaceProductsByCharityId(charityId).subscribe({
      next: (products) => this.charityProductsSubject.next(products),
      error: (err) => console.error('Failed to fetch products:', err)
    });
  }

  getMarketplaceProductsByCharityId(charityId: number): Observable<Product[]> {
    return this.httpClient.get<Product[]>(`${this.productsApiUrl}?acceptsBids=false`).pipe(
      map(products => products.filter(product =>
        product.charityId === charityId
      ))
    );
  }

  getProductsByCharityIdAndStatus(charityId: number, publishStatus: string, acceptsBids?: boolean): Observable<Product[]> {
    const acceptsBidsParam: string = acceptsBids === undefined ? '' : `&acceptsBids=${acceptsBids}`;
    return this.httpClient.get<Product[]>(
      `${this.productsApiUrl}?charityId=${charityId}&publishStatus=${publishStatus}${acceptsBidsParam}`
    );
  }

  loadProductsByAuctionId(auctionId: string): void {
    this.auctionProductsSubject.next([]);
    this.getProductsByAuctionId(auctionId).subscribe({
      next: (products) => this.auctionProductsSubject.next(products),
      error: (err) => console.error('Failed to fetch products:', err)
    })
  }
  
  getProductsByAuctionId(auctionId: string): Observable<Product[]> {
    return this.httpClient.get<Product[]>(`${this.productsApiUrl}?auctionId=${auctionId}&includeOutOfStock=true`);
  }

  // WHY: the products endpoint has no combined auctionId+businessId filter today, so a business's
  // own items within an auction (any status, including PENDING_APPROVAL/DRAFT) are fetched via the
  // existing auctionId filter and narrowed client-side - mirrors getMarketplaceProductsByCharityId's
  // fetch-then-filter shape above.
  getProductsByAuctionIdForBusiness(auctionId: string, businessId: number): Observable<Product[]> {
    return this.getProductsByAuctionId(auctionId).pipe(
      map(products => products.filter(product => product.businessId === businessId))
    );
  }

  getProductByProductId(productId: string): Observable<Product> {
    return this.httpClient.get<Product>(`${this.productsApiUrl}/${productId}`);
  }

  getProductByProductIdIncludeBids(productId: string): Observable<Product> {
    return this.httpClient.get<Product>(`${this.productsApiUrl}/${productId}?includeBids=${true}`);
  }

  deleteProductByProductId(productId: string): Observable<Product> {
    return this.httpClient.delete<Product>(`${this.productsApiUrl}/${productId}`);
  }

  postProduct(formData: FormData): Observable<Product> {
    // Note: Cannot log FormData contents directly, but can log the blob
    const productBlob: FormDataEntryValue | null = formData.get('product');
    if (productBlob instanceof Blob) {
      productBlob.text().then((_text: string) => {
      });
    }
    return this.httpClient.post<Product, FormData>(`${this.productsApiUrl}`, formData);
  }

  editProduct(product: FormData, id: string): Observable<Product> {
    return this.httpClient.put<Product, FormData>(`${this.productsApiUrl}/${id}`, product);
  }

  getFavorites(): Observable<Product[]> {
    return this.httpClient.get<Product[]>(`${this.productsApiUrl}/favorites`);
  }

  postFavoriteByProductId(id: number): Observable<void> {
    return this.httpClient.post<void, number>(`${this.productsApiUrl}/${id}/favorite`, id);
  }

  deleteFavoriteByProductId(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.productsApiUrl}/${id}/favorite`);
  }

  loadActiveBids(): void {
    this.getActiveBids().subscribe({
      next: (products) => {
        this.bidProductsSubject.next(products)
      },
      error: (err) => console.error('Failed to fetch products:', err)
    })
  }

  getActiveBids(): Observable<Product[]> {
    return this.httpClient.getGuest<Product[]>(`${this.productsApiUrl}/active-bids`);
  }

  getWinningProductsByUserId(userId: string): Observable<ProductWinner[]> {
    return this.httpClient.getGuest<ProductWinner[]>(`${this.productWinnersApiUrl}?userId=${userId}`);
  }

  getPendingWinningProductsByCharityId(charityId: string): Observable<ProductWinner[]> {
    return this.httpClient.get<ProductWinner[]>(`${this.productWinnersApiUrl}?charityId=${charityId}`);
  }

  getUnpaidWinningProductsByCharityId(charityId: string, status: string): Observable<ProductWinner[]> {
    return this.httpClient.get<ProductWinner[]>(`${this.productWinnersApiUrl}?charityId=${charityId}&status=${status}`);
  }

  getWinningProductsByUserIdAndStatus(userId: string, status: string): Observable<ProductWinner[]> {
    return this.httpClient.getGuest<ProductWinner[]>(`${this.productWinnersApiUrl}?userId=${userId}&status=${status}`);
  }

  getWinningProductByProductId(productId: string): Observable<ProductWinner> {
    return this.httpClient.getGuest<ProductWinner>(`${this.productWinnersApiUrl}/product/${productId}`);
  }

  postDuplicateProduct(productId: number, auctionId: number): Observable<Product> {
    return this.httpClient.post<Product, number>(`${this.productsApiUrl}/${productId}/duplicate?newAuctionId=${auctionId}`, auctionId);
  }

  deleteWinningProductByProductId(productId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.productWinnersApiUrl}/product/${productId}`);
  }

  postSecondChangeOffer(productId: number, userId: number, option?: UnpaidHandlingOption): Observable<ProductWinner> {
    // Omitting &option preserves the exact query string non-business callers have always sent —
    // the backend defaults to PASS_TO_SECOND_BIDDER when it's absent (SUN-1070 back-compat contract).
    const optionParam: string = option ? `&option=${option}` : '';
    return this.httpClient.post<ProductWinner, number>(`${this.productWinnersApiUrl}/handle-unpaid?userId=${userId}&productId=${productId}${optionParam}`, productId);
  }

  refreshWinningProducts(): void {
    this.winningProductsRefreshSubject.next();
  }

  getProductsByBusinessId(businessId: number, publishStatus?: string): Observable<Product[]> {
    let url: string = `${this.productsApiUrl}?businessId=${businessId}`;
    if (publishStatus) {
      url += `&publishStatus=${publishStatus}`;
    }
    return this.httpClient.get<Product[]>(url);
  }

  updateProductStatus(productId: number, status: string): Observable<Product> {
    return this.httpClient.put<Product, { status: string }>(
      `${this.productsApiUrl}/${productId}/status`,
      { status }
    );
  }
}
